/**
 * placement.ts — boringen van een monsterplan over een locatie verdelen en nummeren.
 *
 * Wat de norm vraagt (hoeveel boringen, hoe diep, hoeveel peilbuizen) staat in nen5740.ts; dit
 * bestand bepaalt alleen WAAR ze komen:
 *   - 'grid'   (vlak): een driehoeksraster dat zo groot mogelijk is en toch genoeg punten in het
 *              vlak laat vallen, met wat afstand tot de rand; daarna gelijkmatig uitgedund tot
 *              het gevraagde aantal. Gericht langs de lange as van het vlak.
 *   - 'line'   (tracé): op gelijke afstanden langs de lange as van het vlak.
 *   - 'source' (puntbron): één in de bron en de rest in een kring eromheen.
 * Peilbuizen komen zo ver mogelijk uit elkaar te liggen. Genummerd wordt in een slingerroute
 * (noord naar zuid, rij voor rij heen en terug), zodat de nummers in het veld logisch lopen.
 *
 * Coördinaten in WGS84 ([lengtegraad, breedtegraad]); gerekend wordt in meters in een vlakke
 * projectie rond het midden, op de schaal van een bodemonderzoek nauwkeurig genoeg.
 */

import type { PlanCounts, PlanLayout } from './nen5740.js';

type XY = [number, number];
const M_PER_DEG = 111_320;

/** Lokale meters rond `origin` (lng/lat) en terug. */
function projectie(origin: XY) {
  const kx = M_PER_DEG * Math.cos((origin[1] * Math.PI) / 180);
  return {
    naar: ([lng, lat]: XY): XY => [(lng - origin[0]) * kx, (lat - origin[1]) * M_PER_DEG],
    terug: ([x, y]: XY): XY => [origin[0] + x / kx, origin[1] + y / M_PER_DEG],
  };
}

const midden = (ring: XY[]): XY => [
  ring.reduce((s, p) => s + p[0], 0) / ring.length,
  ring.reduce((s, p) => s + p[1], 0) / ring.length,
];

function oppervlak(ring: XY[]): number {
  let a = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) a += ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1];
  return Math.abs(a) / 2;
}

function binnen([x, y]: XY, ring: XY[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function afstandTotRand([x, y]: XY, ring: XY[]): number {
  let best = Infinity;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [ax, ay] = ring[j], [bx, by] = ring[i];
    const dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy;
    const t = l2 ? Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / l2)) : 0;
    best = Math.min(best, Math.hypot(x - (ax + t * dx), y - (ay + t * dy)));
  }
  return best;
}

/** Richting van de lange as (radialen), uit de spreiding van de hoekpunten. */
function hoofdas(ring: XY[]): number {
  const [cx, cy] = midden(ring);
  let sxx = 0, syy = 0, sxy = 0;
  for (const [x, y] of ring) { sxx += (x - cx) ** 2; syy += (y - cy) ** 2; sxy += (x - cx) * (y - cy); }
  return 0.5 * Math.atan2(2 * sxy, sxx - syy);
}

const draai = ([x, y]: XY, hoek: number): XY => [x * Math.cos(hoek) - y * Math.sin(hoek), x * Math.sin(hoek) + y * Math.cos(hoek)];

/** Uitdunnen tot n: steeds het punt weghalen dat het dichtst bij een ander ligt. */
function dunUit(punten: XY[], n: number): XY[] {
  const rest = [...punten];
  while (rest.length > n) {
    let slechtste = 0, kleinste = Infinity;
    for (let i = 0; i < rest.length; i++) {
      let d = Infinity;
      for (let j = 0; j < rest.length; j++) if (i !== j) d = Math.min(d, Math.hypot(rest[i][0] - rest[j][0], rest[i][1] - rest[j][1]));
      if (d < kleinste) { kleinste = d; slechtste = i; }
    }
    rest.splice(slechtste, 1);
  }
  return rest;
}

/** Ligt het punt in het vlak en buiten de uitgesloten vlakken? */
const vrij = (p: XY, ring: XY[], uit: XY[][]) => binnen(p, ring) && !uit.some((u) => binnen(p, u));
/** Afstand tot de dichtstbijzijnde rand: van het vlak of van een uitgesloten vlak. */
const randAfstand = (p: XY, ring: XY[], uit: XY[][]) => Math.min(afstandTotRand(p, ring), ...uit.map((u) => afstandTotRand(p, u)));

/**
 * Een punt diep in het vlak (het punt met de grootste afstand tot de rand, op een fijn raster),
 * buiten de uitgesloten vlakken.
 */
function diepstePunt(ring: XY[], uit: XY[][] = []): XY {
  const xs = ring.map((p) => p[0]), ys = ring.map((p) => p[1]);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  let best: XY = midden(ring), bestD = vrij(best, ring, uit) ? randAfstand(best, ring, uit) : -1;
  for (let i = 0; i <= 40; i++) for (let j = 0; j <= 40; j++) {
    const p: XY = [x0 + ((x1 - x0) * i) / 40, y0 + ((y1 - y0) * j) / 40];
    if (!vrij(p, ring, uit)) continue;
    const d = randAfstand(p, ring, uit);
    if (d > bestD) { bestD = d; best = p; }
  }
  return best;
}

/** Het deel van het vlak dat buiten de uitgesloten vlakken valt (0..1), geschat op een raster. */
function vrijeFractie(ring: XY[], uit: XY[][]): number {
  if (!uit.length) return 1;
  const xs = ring.map((p) => p[0]), ys = ring.map((p) => p[1]);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  let binnenRing = 0, vrijeN = 0;
  for (let i = 0; i < 120; i++) for (let j = 0; j < 120; j++) {
    const p: XY = [x0 + ((x1 - x0) * (i + 0.5)) / 120, y0 + ((y1 - y0) * (j + 0.5)) / 120];
    if (!binnen(p, ring)) continue;
    binnenRing++;
    if (!uit.some((u) => binnen(p, u))) vrijeN++;
  }
  return binnenRing ? vrijeN / binnenRing : 1;
}

function rasterInVlak(ring: XY[], n: number, randM: number, uitgesloten: XY[][] = []): XY[] {
  if (n <= 0) return [];
  const hoek = hoofdas(ring);
  const recht = ring.map((p) => draai(p, -hoek));
  const uit = uitgesloten.map((u) => u.map((p) => draai(p, -hoek)));
  if (n === 1) return [draai(diepstePunt(recht, uit), hoek)];
  const xs = recht.map((p) => p[0]), ys = recht.map((p) => p[1]);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  // De maaswijdte volgt het vrije deel: een vlak met een gat erin krijgt een fijner raster.
  const basis = Math.sqrt((oppervlak(recht) * Math.max(vrijeFractie(recht, uit), 0.05)) / n);
  const probeer = (s: number, ox: number, oy: number, rand: number): XY[] => {
    const res: XY[] = [];
    const rij = s * Math.sqrt(3) / 2;
    for (let r = 0, y = y0 + oy * rij; y <= y1; r++, y += rij) {
      for (let x = x0 + ox * s + (r % 2 ? s / 2 : 0); x <= x1; x += s) {
        const p: XY = [x, y];
        if (vrij(p, recht, uit) && randAfstand(p, recht, uit) >= rand) res.push(p);
      }
    }
    return res;
  };
  let gekozen: XY[] | null = null;
  // Van grof naar fijn: de grootste maaswijdte die nog genoeg punten oplevert spreidt het best.
  for (let f = 1.6; f >= 0.25 && !gekozen; f -= 0.03) {
    const s = basis * f;
    const rand = Math.min(randM, s / 3);
    for (const [ox, oy] of [[0.5, 0.5], [0, 0], [0.5, 0], [0, 0.5], [0.25, 0.25], [0.75, 0.75]]) {
      const pts = probeer(s, ox, oy, rand);
      if (pts.length >= n) { gekozen = pts; break; }
    }
  }
  // Een heel smal vlak: zonder randafstand en dan eventueel aanvullen in het midden.
  if (!gekozen) gekozen = probeer(basis * 0.25, 0.5, 0.5, 0);
  if (gekozen.length < n) gekozen = [...gekozen, ...Array.from({ length: n - gekozen.length }, () => diepstePunt(recht, uit))];
  return dunUit(gekozen, n).map((p) => draai(p, hoek));
}

/** Lengte van een tracé: de lengte van het vlak langs zijn lange as (m). */
function langeAs(ring: XY[]): { hoek: number; van: number; tot: number; c: XY } {
  const hoek = hoofdas(ring);
  const c = midden(ring);
  const t = ring.map(([x, y]) => (x - c[0]) * Math.cos(hoek) + (y - c[1]) * Math.sin(hoek));
  return { hoek, van: Math.min(...t), tot: Math.max(...t), c };
}

function langsLijn(ring: XY[], n: number): XY[] {
  const { hoek, van, tot, c } = langeAs(ring);
  const u: XY = [Math.cos(hoek), Math.sin(hoek)], v: XY = [-u[1], u[0]];
  const breed = Math.max(...ring.map(([x, y]) => Math.abs((x - c[0]) * v[0] + (y - c[1]) * v[1]))) * 2 || 1;
  return Array.from({ length: n }, (_, i) => {
    const t = van + ((i + 0.5) * (tot - van)) / n;
    const op: XY = [c[0] + u[0] * t, c[1] + u[1] * t];
    // Midden van het vlak dwars op de as, zodat een gebogen tracé ook goed gaat.
    const binnenPunten: number[] = [];
    for (let k = -50; k <= 50; k++) {
      const s = (k / 50) * breed;
      if (binnen([op[0] + v[0] * s, op[1] + v[1] * s], ring)) binnenPunten.push(s);
    }
    const s = binnenPunten.length ? (Math.min(...binnenPunten) + Math.max(...binnenPunten)) / 2 : 0;
    return [op[0] + v[0] * s, op[1] + v[1] * s] as XY;
  });
}

function rondBron(n: number, straalM: number): XY[] {
  if (n <= 0) return [];
  return [[0, 0], ...Array.from({ length: n - 1 }, (_, i) => {
    const a = (2 * Math.PI * i) / (n - 1) - Math.PI / 2;
    return [Math.cos(a) * straalM, Math.sin(a) * straalM] as XY;
  })];
}

/** k punten zo ver mogelijk uit elkaar (begint bij het punt het dichtst bij het midden). */
function verspreid(punten: XY[], k: number): Set<number> {
  const gekozen = new Set<number>();
  if (!punten.length || k <= 0) return gekozen;
  const c = midden(punten);
  let eerste = 0;
  punten.forEach((p, i) => { if (Math.hypot(p[0] - c[0], p[1] - c[1]) < Math.hypot(punten[eerste][0] - c[0], punten[eerste][1] - c[1])) eerste = i; });
  gekozen.add(eerste);
  while (gekozen.size < Math.min(k, punten.length)) {
    let beste = -1, besteD = -1;
    punten.forEach((p, i) => {
      if (gekozen.has(i)) return;
      const d = Math.min(...[...gekozen].map((j) => Math.hypot(p[0] - punten[j][0], p[1] - punten[j][1])));
      if (d > besteD) { besteD = d; beste = i; }
    });
    gekozen.add(beste);
  }
  return gekozen;
}

/** Slingerroute: rijen van noord naar zuid, om en om van west naar oost en terug. */
function slinger(punten: XY[], rijHoogte: number): number[] {
  const idx = punten.map((_, i) => i);
  const rijVan = (i: number) => Math.round(-punten[i][1] / Math.max(rijHoogte, 0.5));
  idx.sort((a, b) => rijVan(a) - rijVan(b) || punten[a][0] - punten[b][0]);
  const rijen: number[][] = [];
  for (const i of idx) {
    const r = rijVan(i);
    if (!rijen.length || rijVan(rijen[rijen.length - 1][0]) !== r) rijen.push([]);
    rijen[rijen.length - 1].push(i);
  }
  return rijen.flatMap((rij, k) => (k % 2 ? rij.reverse() : rij));
}

export interface PlannedSamplePoint {
  name: string;
  kind: 'boring' | 'peilbuis';
  /** WGS84 [lengtegraad, breedtegraad]. */
  lng: number;
  lat: number;
  depthCm: number | null;
}

export interface PlanInput {
  layout: PlanLayout;
  counts: PlanCounts;
  /** Het vlak (grid, line) als WGS84-ring. */
  ring?: [number, number][];
  /** De bron (source) als WGS84-punt. */
  center?: [number, number];
  /** Straal rond de bron in meters (source). */
  radiusM?: number;
  /** Minimale afstand tot de rand in meters (grid). */
  edgeMarginM?: number;
  /**
   * Vlakken (WGS84-ringen) waar GEEN boringen van dit plan mogen komen (grid): een kleinere
   * deellocatie binnen deze, die zijn eigen boringen heeft. Zo staan er in de overlap geen dubbele.
   */
  exclude?: [number, number][][];
  /** Namen die al bestaan (boringen en geplande punten): de nummering gaat daarna verder. */
  existingNames?: string[];
  boringPrefix?: string;
  peilbuisPrefix?: string;
}

/** Het volgende vrije nummer na de bestaande namen met dit voorvoegsel (B07 → 8). */
function volgendNummer(prefix: string, namen: string[]): number {
  const re = new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*-?(\\d+)$`, 'i');
  return namen.reduce((m, n) => Math.max(m, Number(re.exec(String(n).trim())?.[1]) || 0), 0) + 1;
}

/** Het monsterplan: plekken, soort, diepte en nummer van elke boring. */
export function makeSamplingPlan(input: PlanInput): PlannedSamplePoint[] {
  const { counts } = input;
  const diepten = counts.borings.flatMap((b) => Array.from({ length: b.count }, () => b.depthCm));
  const totaal = diepten.length + counts.peilbuizen;
  if (!totaal) return [];

  let lokaal: XY[];
  let proj: ReturnType<typeof projectie>;
  if (input.layout === 'source') {
    if (!input.center) throw new Error('Een puntbron heeft een middelpunt nodig.');
    proj = projectie(input.center);
    lokaal = rondBron(totaal, input.radiusM ?? 5);
  } else {
    if (!(input.ring && input.ring.length >= 3)) throw new Error('Teken eerst de locatie.');
    proj = projectie(midden(input.ring as XY[]));
    const ring = (input.ring as XY[]).map(proj.naar);
    lokaal = input.layout === 'line' ? langsLijn(ring, totaal)
      : rasterInVlak(ring, totaal, input.edgeMarginM ?? 2, (input.exclude || []).filter((u) => u.length >= 3).map((u) => (u as XY[]).map(proj.naar)));
  }

  // Peilbuizen zo ver mogelijk uit elkaar; de diepste grondboringen ook.
  const pb = verspreid(lokaal, counts.peilbuizen);
  const overige = lokaal.map((_, i) => i).filter((i) => !pb.has(i));
  const volgorde = [...diepten].sort((a, b) => b - a);
  const diep = verspreid(overige.map((i) => lokaal[i]), volgorde.length);
  const diepteVan = new Map<number, number>();
  const diepIdx = [...diep].map((k) => overige[k]);
  const rest = overige.filter((i) => !diepIdx.includes(i));
  [...diepIdx, ...rest].forEach((i, k) => diepteVan.set(i, volgorde[k]));

  // Rijhoogte voor de route: de gewone afstand tot de naaste buur (mediaan).
  const naaste = lokaal.map((p, i) => Math.min(Infinity, ...lokaal.filter((_, j) => j !== i).map((q) => Math.hypot(p[0] - q[0], p[1] - q[1]))))
    .filter(Number.isFinite).sort((a, b) => a - b);
  const spacing = naaste.length ? naaste[Math.floor(naaste.length / 2)] : 5;
  const route = slinger(lokaal, spacing * 0.6);

  const namen = input.existingNames || [];
  const bp = input.boringPrefix ?? 'B', pp = input.peilbuisPrefix ?? 'PB';
  let nb = volgendNummer(bp, namen), np = volgendNummer(pp, namen);
  return route.map((i) => {
    const [lng, lat] = proj.terug(lokaal[i]);
    const isPb = pb.has(i);
    const name = isPb ? `${pp}${String(np++).padStart(2, '0')}` : `${bp}${String(nb++).padStart(2, '0')}`;
    return { name, kind: isPb ? 'peilbuis' : 'boring', lng, lat, depthCm: isPb ? (counts.peilbuisDepthCm ?? null) : (diepteVan.get(i) ?? null) };
  });
}

/** Oppervlakte van een WGS84-ring in m². */
export function ringAreaSquareMeters(ring: [number, number][]): number {
  if (!(ring?.length >= 3)) return 0;
  const proj = projectie(midden(ring as XY[]));
  return oppervlak((ring as XY[]).map(proj.naar));
}

/** Lengte van een tracé (WGS84-ring) langs zijn lange as, in m. */
export function ringLengthMeters(ring: [number, number][]): number {
  if (!(ring?.length >= 3)) return 0;
  const proj = projectie(midden(ring as XY[]));
  const { van, tot } = langeAs((ring as XY[]).map(proj.naar));
  return tot - van;
}

// ── Overlappende deellocaties ────────────────────────────────────────────

function snijden(a: XY, b: XY, c: XY, d: XY): boolean {
  const o = (p: XY, q: XY, r: XY) => Math.sign((q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]));
  return o(a, b, c) !== o(a, b, d) && o(c, d, a) !== o(c, d, b);
}

/** Overlappen twee vlakken (WGS84-ringen)? Ook als het ene helemaal in het andere ligt. */
export function ringsOverlap(a: [number, number][], b: [number, number][]): boolean {
  if (!a || !b || a.length < 3 || b.length < 3) return false;
  const proj = projectie(midden(a as XY[]));
  const ra = (a as XY[]).map(proj.naar), rb = (b as XY[]).map(proj.naar);
  if (ra.some((p) => binnen(p, rb)) || rb.some((p) => binnen(p, ra))) return true;
  for (let i = 0, j = ra.length - 1; i < ra.length; j = i++) {
    for (let k = 0, l = rb.length - 1; k < rb.length; l = k++) if (snijden(ra[j], ra[i], rb[l], rb[k])) return true;
  }
  return false;
}

/** De oppervlakte (m²) van een vlak zonder de delen die in de uitgesloten vlakken liggen. */
export function areaOutsideM2(ring: [number, number][], exclude: [number, number][][] = []): number {
  if (!ring || ring.length < 3) return 0;
  const proj = projectie(midden(ring as XY[]));
  const r = (ring as XY[]).map(proj.naar);
  const uit = exclude.filter((u) => u?.length >= 3).map((u) => (u as XY[]).map(proj.naar));
  return oppervlak(r) * vrijeFractie(r, uit);
}
